import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

const STEPS = [
  { img: `${process.env.PUBLIC_URL}/install_android1.png`, captionKey: "androidInstallStep1" },
  { img: `${process.env.PUBLIC_URL}/install_android2.png`, captionKey: "androidInstallStep2" },
];

const SWIPE_THRESHOLD = 50; // px

const AndroidInstallModal = ({ onClose }) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(null);

  const prev = () => setCurrent((c) => Math.max(0, c - 1));
  const next = () => setCurrent((c) => Math.min(STEPS.length - 1, c + 1));

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (delta > SWIPE_THRESHOLD) next();
    else if (delta < -SWIPE_THRESHOLD) prev();
    touchStartX.current = null;
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        {/* Close button */}
        <button onClick={onClose} style={closeBtn} aria-label="close">✕</button>

        {/* Instruction sentence — centered + bold */}
        <p style={hint}>{t("androidInstallHint")}</p>

        {/* Slider */}
        <div style={sliderWrap}>
          {/* Prev arrow */}
          <button
            onClick={prev}
            disabled={current === 0}
            style={{ ...arrow, opacity: current === 0 ? 0.2 : 1 }}
            aria-label="previous"
          >
            ‹
          </button>

          {/* Image — swipeable */}
          <div
            style={imgWrap}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <img
              src={STEPS[current].img}
              alt={`step ${current + 1}`}
              style={img}
              draggable={false}
            />
          </div>

          {/* Next arrow */}
          <button
            onClick={next}
            disabled={current === STEPS.length - 1}
            style={{ ...arrow, opacity: current === STEPS.length - 1 ? 0.2 : 1 }}
            aria-label="next"
          >
            ›
          </button>
        </div>

        {/* Step caption */}
        <p style={stepCaption}>{t(STEPS[current].captionKey)}</p>

        {/* Dots */}
        <div style={dots}>
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              style={{ ...dot, background: i === current ? "#333" : "#ccc" }}
              aria-label={`go to step ${i + 1}`}
            />
          ))}
        </div>

        {/* Step counter */}
        <p style={counter}>{current + 1} / {STEPS.length}</p>

        {/* Close button at bottom */}
        <button onClick={onClose} style={closeBtnBottom}>
          {t("iosInstallClose")}
        </button>
      </div>
    </div>
  );
};

/* ── styles ── */
const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};

const modal = {
  background: "#fff",
  borderRadius: 18,
  padding: "24px 18px 18px",
  width: "92vw",
  maxWidth: 440,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 14,
  position: "relative",
  boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
};

const closeBtn = {
  position: "absolute",
  top: 12,
  right: 14,
  background: "none",
  border: "none",
  fontSize: 22,
  color: "#888",
  cursor: "pointer",
  lineHeight: 1,
};

const hint = {
  margin: 0,
  fontSize: 15,
  fontWeight: "bold",
  color: "#222",
  textAlign: "center",
  width: "100%",
  paddingInline: 28,
};

const sliderWrap = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  width: "100%",
};

const imgWrap = {
  flex: 1,
  display: "flex",
  justifyContent: "center",
  borderRadius: 12,
  overflow: "hidden",
  background: "#f0f0f0",
  minHeight: 300,
  touchAction: "pan-y",
  userSelect: "none",
};

const img = {
  width: "100%",
  objectFit: "contain",
  maxHeight: 420,
  pointerEvents: "none",
};

const arrow = {
  background: "none",
  border: "none",
  fontSize: 42,
  color: "#333",
  cursor: "pointer",
  padding: "0 2px",
  lineHeight: 1,
  flexShrink: 0,
};

const dots = {
  display: "flex",
  gap: 10,
};

const dot = {
  width: 10,
  height: 10,
  borderRadius: "50%",
  border: "none",
  cursor: "pointer",
  padding: 0,
  transition: "background 0.2s",
};

const stepCaption = {
  margin: 0,
  fontSize: 14,
  color: "#444",
  textAlign: "center",
  paddingInline: 12,
  lineHeight: 1.5,
};

const counter = {
  margin: 0,
  fontSize: 13,
  color: "#999",
};

const closeBtnBottom = {
  width: "100%",
  padding: "12px 0",
  border: "none",
  borderRadius: 10,
  background: "#333",
  color: "#fff",
  fontSize: 16,
  cursor: "pointer",
};

export default AndroidInstallModal;
